/**
 * Mega Menu (desktop)
 * Handles open/close of full-width dropdown panels triggered by the nav bar buttons.
 * Hover-intent, click, keyboard, focus trap, Escape and a blurred backdrop.
 *
 * The nav bar is rendered inline in the Header section's menu slot; the dropdown panels
 * are reparented into the [data-menu-dropdowns] portal at the header root and positioned
 * flush below #header-component. Sticky behaviour is inherited from the header itself, so
 * this script no longer manages a standalone sticky band.
 *
 * Ported from the Digi Direct mega menu; the sidebar category-switching and
 * hover-swapped feature image have been removed (panels are flat and static).
 */

import { debounce, setHeaderMenuStyle } from '@theme/utilities';

(function () {
// Read the transition duration from the theme's --animation-speed token once at
// script load, so hover-intent and hide timers match the CSS without a hardcoded
// literal. (Resolved at load, not per-scroll — a runtime token change needs a
// reload.) --animation-speed is in seconds (e.g. "0.125s"); fall back to 125ms.
const TRANSITION_MS =
  parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--animation-speed')) * 1000 || 125;
const HOVER_INTENT_MS = 150;
const FOCUS_DELAY_MS = 50;
// Tracks the pending transitionend "hide" listener per dropdown without storing
// state on the DOM node (expando) — keyed weakly so entries GC with the element.
const transitionEndHandlers = new WeakMap();

class MegaMenuManager {
  /**
   * Create a new MegaMenuManager and initialise all subsystems.
   */
  constructor() {
    this.activeMenu = null;
    this.hoverTimeout = null;
    this.closeTimeout = null;
    this.focusTrapHandler = null;
    this._blockSelectHandler = null;
    this._backdropHandler = null;
    this._escHandler = null;
    this._resizeHandler = null;
    this._pointerFocusHandler = null;
    /** @type {Element | null} */
    this._pointerFocusedControl = null;
    this.backdrop = document.querySelector('[data-menu-backdrop]');
    // The header element anchors the backdrop: panels sit flush below it, so the dimmed
    // overlay starts at the header's bottom edge.
    this.headerEl = document.querySelector('#header-component');
    this.init();
  }

  /**
   * Initialise all subsystems on page load.
   * @returns {void}
   */
  init() {
    this.reparentDropdowns();
    this.setupTriggers();
    this.setupDropdowns();
    this.setupKeyboard();
    this.setupEditorEvents();
    this.setupMenuStyleResize();
    this.setupPointerFocusTracking();
  }

  /**
   * Keep the header's data-menu-style attribute in step with the viewport.
   *
   * setHeaderMenuStyle() picks between the inline menu and the drawer layout, and for a
   * touch device that choice now depends on the mobile breakpoint (see utilities.js). It
   * runs once on load; the debounced resize listener that re-runs it lives on the
   * `header-menu` custom element, which the header does not render when the mega menu
   * replaces the built-in nav — so nothing re-evaluated it here. Without this, a phone
   * rotated from portrait to landscape crosses 750px while the attribute still says
   * `drawer`, which is the layout that has no room for this nav.
   * @returns {void}
   */
  setupMenuStyleResize() {
    this._resizeHandler = debounce(() => {
      setHeaderMenuStyle();

      /* Crossing below the breakpoint hides the nav bar, but an open panel has been
         reparented into the header-root portal and is not a descendant of it, so it would
         stay visible and focusable with its trigger gone — an orphaned role="dialog" whose
         aria-controls points at a hidden element. Close it here rather than leaning on the
         CSS safety net in header-mega-menu.liquid, so focus and aria-expanded unwind
         properly. Rotating a phone to landscape and back is the realistic trigger. */
      if (this.activeMenu && !window.matchMedia('(min-width: 750px)').matches) {
        const dropdown = document.querySelector(`[data-dropdown-for="${this.activeMenu.getAttribute('data-menu-id')}"]`);
        const heldFocus = this.activeMenu.contains(document.activeElement) || !!dropdown?.contains(document.activeElement);
        this.closeMenu(this.activeMenu);

        /* closeMenu() does not restore focus (only the Escape handler does), and the
           element holding it is about to be display:none — which drops focus to the body.
           Hand it to the hamburger instead, the control that replaces this nav. Deferred a
           frame because setHeaderMenuStyle() writes data-menu-style inside its own
           requestAnimationFrame, and the button is not focusable until that lands; our
           callback is queued after it. */
        if (heldFocus) {
          window.requestAnimationFrame(() => {
            /** @type {HTMLElement | null} */
            const hamburger = document.querySelector('[data-mobile-mega-menu-trigger]');
            hamburger?.focus();
          });
        }
      }
    }, 100);
    window.addEventListener('resize', this._resizeHandler);
  }

  /**
   * Move [data-megamenu-dropdown] elements into the portal and wrap promo tiles in a grid container.
   * @returns {void}
   */
  reparentDropdowns() {
    const portal = document.querySelector('[data-menu-dropdowns]');
    const buttons = document.querySelector('[data-menu-buttons]');
    if (!portal || !buttons) return;

    buttons.querySelectorAll('[data-megamenu-dropdown]').forEach((dropdown) => {
      portal.appendChild(dropdown);
    });

    portal.querySelectorAll('.mega-menu__panel').forEach((panel) => {
      if (panel.querySelector(':scope > .mega-menu__promos')) return;
      const promos = [...panel.querySelectorAll(':scope > .mega-menu__promo')];
      if (promos.length === 0) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'mega-menu__promos';
      panel.insertBefore(wrapper, promos[0]);
      promos.forEach((p) => wrapper.appendChild(p));
    });
  }

  /**
   * Register Shopify theme editor events so the menu stays functional after section/block re-renders.
   * @returns {void}
   */
  setupEditorEvents() {
    document.addEventListener('shopify:section:load', () => {
      window.__megaMenuManager?.destroy();
      window.__megaMenuManager = new MegaMenuManager();
    }, { once: true });
    this._blockSelectHandler = () => this.reparentDropdowns();
    document.addEventListener('shopify:block:select', this._blockSelectHandler);
  }

  /**
   * Return visible focusable elements within a container.
   * @param {HTMLElement} container
   * @returns {HTMLElement[]}
   */
  getFocusableElements(container) {
    const selectors =
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(container.querySelectorAll(selectors)).filter((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return false;
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  }

  /**
   * Attach a Tab-key focus trap spanning the dropdown and its trigger button.
   * @param {HTMLElement} dropdown
   * @param {HTMLElement} [trigger]
   * @returns {void}
   */
  setupFocusTrap(dropdown, trigger) {
    this.removeFocusTrap();

    this.focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;

      const inDropdown = dropdown.contains(document.activeElement);
      const onTrigger = trigger && document.activeElement === trigger;
      if (!inDropdown && !onTrigger) return;

      const focusable = this.getFocusableElements(dropdown);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        // Shift+Tab from first dropdown element → return focus to trigger
        if (inDropdown && document.activeElement === first) {
          e.preventDefault();
          if (trigger) trigger.focus();
        }
        // Shift+Tab on trigger → wrap to last dropdown element
        else if (onTrigger) {
          e.preventDefault();
          last.focus();
        }
      } else if (onTrigger) {
        // Tab on trigger → enter dropdown at first element
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', this.focusTrapHandler);
  }

  /**
   * Remove the active focus trap keydown handler.
   * @returns {void}
   */
  removeFocusTrap() {
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }

  /**
   * Track form controls the pointer put focus on, so a hover-opened panel can dismiss their
   * native popup without touching focus a keyboard user placed.
   *
   * A `<select>` (or a date input) renders its popup as an OS-level widget outside the page's
   * compositing tree: no z-index or stacking context can cover it, so it hangs over the panel
   * until something dismisses it. Reported on the cart page — open a line item's quantity
   * dropdown, choose nothing, hover the nav.
   *
   * Blurring is the only way to close it, and nothing in CSS or the DOM reports whether a
   * popup is actually open. `:focus-visible` cannot separate the two cases either — measured
   * live, it is true for a mouse-clicked select as well as a tabbed-to one. So the pointer is
   * tracked directly: only a control this listener saw the pointer press is a candidate, which
   * leaves a keyboard user's focus alone.
   * @returns {void}
   */
  setupPointerFocusTracking() {
    this._pointerFocusHandler = (event) => {
      const target = event.target;
      this._pointerFocusedControl =
        target instanceof Element ? target.closest('select, input[type="date"]') : null;
    };
    // Capture phase: a handler on the control that stops propagation must not hide the press.
    document.addEventListener('pointerdown', this._pointerFocusHandler, true);
  }

  /**
   * Dismiss the native popup of a control the pointer focused, if it still holds focus.
   *
   * Identity-checked against `document.activeElement`, so a stale reference is inert once
   * focus has moved on. Deliberately not "suppress the hover-open while any select is
   * focused": a select keeps focus after its popup closes (the usual state right after
   * choosing a value), and refusing to open the panel there would leave the nav looking dead
   * in a very ordinary case.
   * @returns {void}
   */
  dismissPointerOpenedPopup() {
    const control = this._pointerFocusedControl;
    if (control instanceof HTMLElement && document.activeElement === control) {
      control.blur();
      this._pointerFocusedControl = null;
    }
  }

  /**
   * Attach hover, click, and keyboard listeners to all menu item wrappers and the backdrop.
   * @returns {void}
   */
  setupTriggers() {
    if (this.backdrop) {
      this._backdropHandler = () => {
        if (this.activeMenu) this.closeMenu(this.activeMenu);
      };
      this.backdrop.addEventListener('click', this._backdropHandler);
    }

    document.querySelectorAll('.mega-menu-item').forEach((menuWrapper) => {
      const button = menuWrapper.querySelector('[data-megamenu-trigger]');
      if (!button) return;

      menuWrapper.addEventListener('mouseenter', () => {
        clearTimeout(this.closeTimeout);
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = setTimeout(() => {
          this.dismissPointerOpenedPopup();

          if (this.activeMenu && this.activeMenu !== menuWrapper) {
            this.closeMenu(this.activeMenu);
          }
          this.openMenu(menuWrapper);
        }, HOVER_INTENT_MS);
      });

      menuWrapper.addEventListener('mouseleave', () => {
        clearTimeout(this.hoverTimeout);
        this.closeTimeout = setTimeout(() => this.closeMenu(menuWrapper), TRANSITION_MS);
      });

      button.addEventListener('click', (e) => {
        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        // If the trigger is a real link (has an href) and hover has already opened its
        // panel, let the click navigate through instead of just re-closing the dropdown.
        if (isExpanded && button.hasAttribute('href')) return;

        e.preventDefault();
        if (isExpanded) {
          this.closeMenu(menuWrapper);
        } else {
          if (this.activeMenu && this.activeMenu !== menuWrapper) {
            this.closeMenu(this.activeMenu);
          }
          this.openMenu(menuWrapper);
        }
      });
    });
  }

  /**
   * Attach mouseenter/mouseleave listeners to dropdown panels for hover-intent close cancellation.
   * @returns {void}
   */
  setupDropdowns() {
    document.querySelectorAll('[data-megamenu-dropdown]').forEach((dropdown) => {
      dropdown.addEventListener('mouseenter', () => {
        clearTimeout(this.closeTimeout);
        clearTimeout(this.hoverTimeout);
      });

      dropdown.addEventListener('mouseleave', () => {
        clearTimeout(this.hoverTimeout);
        const dropdownFor = dropdown.getAttribute('data-dropdown-for');
        const menuWrapper = document.querySelector(`[data-menu-id="${dropdownFor}"]`);
        this.closeTimeout = setTimeout(() => {
          // Don't yank the panel closed if a keyboard user has Tabbed into it.
          if (dropdown.contains(document.activeElement)) return;
          if (menuWrapper) this.closeMenu(menuWrapper);
        }, TRANSITION_MS);
      });
    });
  }

  /**
   * Attach Space/Enter open handlers to triggers and a global Escape close handler.
   * @returns {void}
   */
  setupKeyboard() {
    document.querySelectorAll('.mega-menu-item').forEach((menuWrapper) => {
      const button = menuWrapper.querySelector('[data-megamenu-trigger]');
      if (!button) return;

      button.addEventListener('keydown', (e) => {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        // Mirror the click handler: Enter on an already-open link trigger should navigate,
        // same as a mouse click, instead of just re-closing the dropdown (Space has no
        // native link action, so it always keeps toggling).
        if (e.key === 'Enter' && isExpanded && button.hasAttribute('href')) return;

        e.preventDefault();
        if (isExpanded) {
          this.closeMenu(menuWrapper);
          return;
        }
        if (this.activeMenu && this.activeMenu !== menuWrapper) {
          this.closeMenu(this.activeMenu);
        }
        this.openMenu(menuWrapper);

        const menuId = menuWrapper.getAttribute('data-menu-id');
        const dropdown = document.querySelector(`[data-dropdown-for="${menuId}"]`);
        if (dropdown) {
          setTimeout(() => {
            const firstFocusable = this.getFocusableElements(dropdown)[0];
            if (firstFocusable) firstFocusable.focus();
          }, FOCUS_DELAY_MS);
        }
      });
    });

    this._escHandler = (e) => {
      if (e.key === 'Escape' && this.activeMenu) {
        const button = this.activeMenu.querySelector('[data-megamenu-trigger]');
        this.closeMenu(this.activeMenu);
        if (button) button.focus();
      }
    };
    document.addEventListener('keydown', this._escHandler);
  }

  /**
   * Open the dropdown for the given menu item.
   * @param {HTMLElement} menu - A `.mega-menu-item` wrapper element.
   * @returns {void}
   */
  openMenu(menu) {
    const menuId = menu.getAttribute('data-menu-id');
    const dropdown = document.querySelector(`[data-dropdown-for="${menuId}"]`);
    const trigger = menu.querySelector('[data-megamenu-trigger]');
    if (!dropdown) return;

    menu.classList.add('mega-menu-item--active');
    dropdown.removeAttribute('hidden');
    // Panels are hidden and re-shown rather than re-rendered, and a tall one scrolls
    // internally (see the max-block-size cap in mega-menu-panel.liquid), so a panel left
    // scrolled down would reopen mid-list with its first group above the fold. Reset while
    // still hidden — doing it on close would animate the jump during the fade-out.
    dropdown.scrollTop = 0;
    // Force reflow so the transition runs after `hidden` is cleared.
    void dropdown.offsetHeight;
    dropdown.classList.add('mega-menu__dropdown--visible');

    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    this.setupFocusTrap(dropdown, trigger);

    if (this.backdrop) {
      if (this.headerEl) {
        const headerBottom = this.headerEl.getBoundingClientRect().bottom;
        this.backdrop.style.setProperty('--menu-backdrop-top', `${headerBottom}px`);
      }
      this.backdrop.classList.add('mega-menu-backdrop--visible');
    }

    this.activeMenu = menu;
  }

  /**
   * Close the dropdown for the given menu item.
   * @param {HTMLElement} menu - A `.mega-menu-item` wrapper element.
   * @returns {void}
   */
  closeMenu(menu) {
    const menuId = menu.getAttribute('data-menu-id');
    const dropdown = document.querySelector(`[data-dropdown-for="${menuId}"]`);
    const trigger = menu.querySelector('[data-megamenu-trigger]');
    if (!dropdown) return;

    // Cancel the pending hover-intent open before closing, or it reopens the panel the
    // user just dismissed: the mouseenter timer calls openMenu() unconditionally, and its
    // "another menu is active" guard does not fire once activeMenu has been cleared. A tap
    // synthesises mouseenter and click in the same tick, so on touch the timer is always
    // in flight when the second tap closes the panel — reproducible there, and with a
    // mouse too on a quick hover-click-click.
    clearTimeout(this.hoverTimeout);
    clearTimeout(this.closeTimeout);

    menu.classList.remove('mega-menu-item--active');
    dropdown.classList.remove('mega-menu__dropdown--visible');

    // Cancel any previous pending hide listener
    const pending = transitionEndHandlers.get(dropdown);
    if (pending) {
      dropdown.removeEventListener('transitionend', pending);
    }
    const handleTransitionEnd = () => {
      if (!dropdown.classList.contains('mega-menu__dropdown--visible')) {
        dropdown.setAttribute('hidden', '');
      }
      transitionEndHandlers.delete(dropdown);
    };
    transitionEndHandlers.set(dropdown, handleTransitionEnd);
    dropdown.addEventListener('transitionend', handleTransitionEnd, { once: true });

    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    this.removeFocusTrap();

    if (this.backdrop) {
      this.backdrop.classList.remove('mega-menu-backdrop--visible');
    }

    if (this.activeMenu === menu) this.activeMenu = null;
  }

  /**
   * Remove all document-level listeners so this instance can be safely replaced.
   * @returns {void}
   */
  destroy() {
    clearTimeout(this.hoverTimeout);
    clearTimeout(this.closeTimeout);
    this.removeFocusTrap();
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }
    if (this._pointerFocusHandler) {
      document.removeEventListener('pointerdown', this._pointerFocusHandler, true);
      this._pointerFocusHandler = null;
    }
    this._pointerFocusedControl = null;
    if (this._backdropHandler) {
      this.backdrop?.removeEventListener('click', this._backdropHandler);
      this._backdropHandler = null;
    }
    if (this._escHandler) {
      document.removeEventListener('keydown', this._escHandler);
      this._escHandler = null;
    }
    if (this._blockSelectHandler) {
      document.removeEventListener('shopify:block:select', this._blockSelectHandler);
      this._blockSelectHandler = null;
    }
  }
}

if (!window.__megaMenuManager) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.__megaMenuManager = new MegaMenuManager();
    });
  } else {
    window.__megaMenuManager = new MegaMenuManager();
  }
}
})();
